import classNames from "classnames"

import NumberControls, {
  usePurchasableActions,
} from "./components/NumberControls"

import Link from "@components/elements/Link"
import Badge from "@components/elements/V2/Badge"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import NumberDisplay from "@components/elements/V2/NumberDisplay"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/elements/V2/Video"
import ProductRating from "@components/experiments/Widgets/ProductRating"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import { PurchasableItem } from "@hooks/V2/storyblok"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import { useIsBreakpoint } from "@utils/V2/screen"
import React, { useCallback, useState } from "react"

type Props = {
  superScript?: string
  imageUrl: string
  imageAlt: string
  thumbnailVideoUrl?: string
  productName: string
  shortDescription?: string | React.ReactNode
  priceValue?: string | number
  priceLabelOverride?: YocoCom.PriceLabelOverride
  priceAdornmentText?: string
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
  learnMoreButtonUrl?: string | YocoCom.YocoLink
  backgroundColor?: Color
  mobileGridOrder?: number
  isSliderChild?: boolean
  hidePrice?: boolean
  siblingCount?: number
  className?: string
  hasBorder?: boolean
  wasPrice?: string | number
  showNumberControls?: boolean
  purchasableItem?: PurchasableItem
  badge?: YocoCom.Badge
  reviewSkus?: string[]
  showRating?: boolean
}

const ProductCard = ({
  mobileGridOrder,
  superScript,
  imageUrl,
  imageAlt,
  thumbnailVideoUrl,
  productName,
  shortDescription,
  priceValue,
  priceLabelOverride,
  priceAdornmentText = "R",
  ctaText = "Buy Now",
  ctaLink,
  learnMoreButtonUrl,
  backgroundColor = Color.White,
  isSliderChild,
  hidePrice,
  siblingCount = 3,
  className,
  hasBorder = true,
  wasPrice,
  showNumberControls,
  purchasableItem,
  badge,
  reviewSkus,
  showRating = true,
  ...props
}: Props) => {
  const accentColor = getAccentColor(backgroundColor)
  const isDarkBackground = accentColor === Color.White
  const isDesktop = useIsBreakpoint("desktop")

  const [videoContainerHover, setVideoContainerHover] = useState(false)

  const onMouseEnter = useCallback(() => setVideoContainerHover(true), [])
  const onMouseLeave = useCallback(() => setVideoContainerHover(false), [])

  const { handleIncrement } = usePurchasableActions(purchasableItem)

  const showIncrementOverlay = Boolean(showNumberControls && purchasableItem)
  const overlayLink = showIncrementOverlay
    ? undefined
    : (ctaLink ?? learnMoreButtonUrl)
  const hasInteractiveOverlay = showIncrementOverlay || Boolean(overlayLink)

  const renderOverlay = () => {
    if (showIncrementOverlay) {
      return (
        <button
          type="button"
          onClick={handleIncrement}
          onMouseDown={(e) => e.preventDefault()}
          aria-label={`Add ${productName} to cart`}
          className="absolute inset-0 z-10"
        >
          <span className="sr-only">Add to cart</span>
        </button>
      )
    }
    if (overlayLink) {
      return (
        <Link
          link={overlayLink}
          draggable={false}
          onMouseDown={(e) => e.preventDefault()}
          aria-label={
            ctaLink
              ? `${ctaText} ${productName}`
              : `Learn more about ${productName}`
          }
          className="absolute inset-0 z-10"
        >
          <span className="sr-only">{ctaLink ? ctaText : "Learn more"}</span>
        </Link>
      )
    }
    return null
  }

  const getColSpan = () => {
    if (siblingCount === 1) return "sm:col-span-12"
    if (siblingCount === 2)
      return "sm:col-span-2 md:col-span-3 lg:col-span-6 lg:first:mr-0 lg:last:ml-0"
    return "sm:col-span-2 md:col-span-3 lg:col-span-4 xl:first:mr-0 xl:last:ml-0"
  }

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      isSliderChild={isSliderChild}
      onMouseEnter={thumbnailVideoUrl ? onMouseEnter : undefined}
      onMouseLeave={thumbnailVideoUrl ? onMouseLeave : undefined}
      className={classNames(
        "relative col-span-4 mx-auto mb-20 flex w-full max-w-[335px] flex-col overflow-hidden rounded-20 transition-[border-color,box-shadow] duration-300 sm:max-w-[388px]",
        getBackgroundColorClass(backgroundColor),
        getColSpan(),
        hasBorder && [
          "border shadow-[0_10px_20px_rgba(0,0,0,0.05)]",
          isDarkBackground ? "border-white/10" : "border-charcoal/10",
        ],
        hasInteractiveOverlay &&
          hasBorder && [
            isDarkBackground
              ? "hover:border-white/50"
              : "hover:border-charcoal/50",
            "hover:shadow-[0_12px_18px_-4px_rgba(0,0,0,0.14)]",
          ],
        className
      )}
      {...props}
    >
      {renderOverlay()}

      <div className="relative flex h-[230px] flex-none items-center justify-center sm:h-[270px]">
        {badge && (
          <Badge
            text={badge.text}
            highlightedText={badge.highlightedText}
            textColor={badge.textColor ?? Color.White}
            accentColor={badge.color}
            style="solid"
            isRounded
            uppercaseText={false}
            className="pointer-events-none absolute left-16 top-16 z-10"
          />
        )}
        {superScript && (
          <div className="pointer-events-none absolute left-1/2 top-16 z-10 -translate-x-1/2">
            <Typography
              text={superScript}
              font="grotesk"
              weight="book"
              size="body-sm"
              color={accentColor}
              className="text-nowrap opacity-50"
            />
          </div>
        )}

        {thumbnailVideoUrl ? (
          <Video
            video={thumbnailVideoUrl}
            coverImage={imageUrl}
            ImageAlt={imageAlt || "Product image"}
            loadType="eager"
            aspectRatio="1:1"
            playbackMode={isDesktop ? "hover" : "autoplay"}
            videoContainerHover={isDesktop ? videoContainerHover : true}
            resetOnLeave
            rounded
            width={200}
            className="max-w-[200px]"
          />
        ) : (
          <GatsbyStoryblokImage
            aspectRatio="1:1"
            loadType="eager"
            image={imageUrl}
            alt={imageAlt || "Product image"}
            width={200}
            testId="card-machine-card-image"
            className="max-w-[200px]"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col items-start gap-16 px-24 pb-32 pt-16 sm:gap-24 sm:px-14 sm:pb-48 md:px-40">
        <div className="flex w-full flex-col items-start gap-4">
          <Typography
            font="grotesk"
            weight="medium"
            size="body-lg-fixed"
            color={accentColor}
            text={productName}
          />

          {priceLabelOverride ? (
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {priceLabelOverride.caption && (
                <Typography
                  font="grotesk"
                  weight="medium"
                  size="body-lg"
                  color={accentColor}
                  text={priceLabelOverride.caption}
                />
              )}

              <Typography
                font="grotesk"
                weight="book"
                size="body-sm"
                color={accentColor}
                text={priceLabelOverride.label}
                className="opacity-60"
              />
            </div>
          ) : (
            !hidePrice && (
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
                <NumberDisplay
                  type="price"
                  size="matchFont"
                  value={priceValue}
                  valueFontSize="body-lg-fixed"
                  adornmentText={priceAdornmentText}
                  adornmentTextPosition="left"
                  textColor={accentColor}
                />

                {wasPrice != null && (
                  <Typography
                    font="grotesk"
                    weight="book"
                    size="body-sm"
                    color={accentColor}
                    text={`${priceAdornmentText}${wasPrice}`}
                    className="line-through opacity-80"
                  />
                )}
              </div>
            )
          )}
        </div>

        {showRating && reviewSkus != null && reviewSkus.length > 0 && (
          <div className="-mt-16 flex h-16 items-center gap-8">
            <ProductRating
              skus={reviewSkus}
              color={Color.Blue}
              textColor={accentColor}
            />
          </div>
        )}

        {typeof shortDescription === "string" && shortDescription && (
          <Typography
            font="grotesk"
            weight="book"
            size="body-sm"
            color={accentColor}
            text={shortDescription}
            className="text-left"
          />
        )}

        {shortDescription && typeof shortDescription !== "string" && (
          <div className="w-full text-left">{shortDescription}</div>
        )}

        {showNumberControls && purchasableItem && (
          <NumberControls
            purchasableItem={purchasableItem}
            accentColor={accentColor}
          />
        )}

        {!showNumberControls && (ctaLink || learnMoreButtonUrl) && (
          <div className="relative z-10 mt-auto flex w-full flex-col gap-16 md:flex-row">
            {ctaLink && (
              <PillButton
                size="medium"
                text={ctaText}
                style="solid"
                color={Color.Blue}
                linkUrl={ctaLink}
                className="!w-full flex-1 text-nowrap"
              />
            )}
            {learnMoreButtonUrl && (
              <PillButton
                hideIcon
                size="medium"
                text="Learn more"
                style="bordered"
                color={Color.Blue}
                overrideTextColor={accentColor}
                linkUrl={learnMoreButtonUrl}
                className="!w-full flex-1 text-nowrap"
              />
            )}
          </div>
        )}
      </div>
    </LayoutItem>
  )
}

export default ProductCard
